import React from "react";

import { AlertCircle } from "lucide-react";

import { FieldStatusIcon } from "../shared/FieldStatusIcon";
import { FieldWithAutoFill } from "../shared/FieldWithAutoFill";

interface ExperimentContextFieldProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  getFieldStatus: (value: string) => string;
  isRecording: boolean;
  isSummarizing: boolean;
  canSummarize: boolean;
  onVoiceRecord: () => void;
  onSummarize: () => void;
  placeholderText?: string;
  onFieldHelp?: (fieldType: string) => void;
}

export const ExperimentContextField: React.FC<ExperimentContextFieldProps> = ({
  value,
  onChange,
  error,
  getFieldStatus,
  isRecording,
  isSummarizing,
}) => {
  const placeholder =
    "Premium cardholders experience significant friction during credit card application upgrades-our current 12-step process causes 40% drop-off rates and average completion times exceeding 25 minutes. Recent customer feedback surveys and declining Q3 conversion rates make solving this a priority. We're testing a streamlined 5-step flow with real-time document verification and instant pre-qualification feedback.";

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <FieldStatusIcon status={getFieldStatus(value)} />
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold text-white">
              Context &amp; background <span className="text-green-500">*</span>
            </span>
          </div>
        </div>
      </div>

      <FieldWithAutoFill
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        suggestedValue={placeholder}
        type="textarea"
        rows={5}
        required
      />

      <p className="text-sm text-white/60">
        What problem are you solving and why now?
      </p>

      {error && (
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-red-400" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {isRecording && (
        <p className="flex items-center gap-2 text-sm text-amber-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          Listening... FalconAI is transcribing your description
        </p>
      )}

      {isSummarizing && (
        <p className="flex items-center gap-2 text-sm text-amber-300">
          <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" />
          FalconAI is creating an executive summary...
        </p>
      )}
    </div>
  );
};
